import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardMedia
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface Notice {
  id: string;
  title: string;
  description: string;
  date: string;
  category: 'general' | 'urgent' | 'event' | 'closure' | 'other';
  validUntil: string;
  imageUrl?: string;
}

export default function NoticesManager() {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Form fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [category, setCategory] = useState<Notice['category']>('general');
  const [validUntil, setValidUntil] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imagePreview, setImagePreview] = useState<string>('');

  useEffect(() => {
    loadNotices();
  }, []);

  const loadNotices = async () => {
    try {
      setLoading(true);
      console.log('Loading notices from Firebase...');
      const querySnapshot = await getDocs(collection(db, 'news_section_notices'));
      const data: Notice[] = [];
      querySnapshot.forEach((doc) => {
        data.push({ id: doc.id, ...doc.data() } as Notice);
      });
      data.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      console.log(`Loaded ${data.length} notices:`, data);
      setNotices(data);
    } catch (error) {
      console.error('Error loading notices:', error);
      setSnackbar({ open: true, message: 'Error loading notices', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim() || !description.trim()) {
      setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
      return;
    }

    try {
      const docId = editingId || `notice_${Date.now()}`;
      const docRef = doc(db, 'news_section_notices', docId);
      
      const noticeData: Omit<Notice, 'id'> = {
        title: title.trim(),
        description: description.trim(),
        date,
        category,
        validUntil: validUntil || '',
        imageUrl: imageUrl || ''
      };

      await setDoc(docRef, noticeData);
      console.log('Notice saved:', docId, noticeData);
      
      setSnackbar({ 
        open: true, 
        message: editingId ? 'Notice updated successfully!' : 'Notice added successfully!', 
        severity: 'success' 
      });
      
      resetForm();
      setDialogOpen(false);
      loadNotices();
    } catch (error) {
      console.error('Error saving notice:', error);
      setSnackbar({ open: true, message: 'Error saving notice', severity: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this notice?')) return;

    try {
      await deleteDoc(doc(db, 'news_section_notices', id));
      console.log('Notice deleted:', id);
      setSnackbar({ open: true, message: 'Notice deleted successfully!', severity: 'success' });
      loadNotices();
    } catch (error) {
      console.error('Error deleting notice:', error);
      setSnackbar({ open: true, message: 'Error deleting notice', severity: 'error' });
    }
  };

  const handleEdit = (notice: Notice) => {
    setEditingId(notice.id);
    setTitle(notice.title);
    setDescription(notice.description);
    setDate(notice.date);
    setCategory(notice.category);
    setValidUntil(notice.validUntil || '');
    setImageUrl(notice.imageUrl || '');
    setImagePreview(notice.imageUrl || '');
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingId(null);
    setTitle('');
    setDescription('');
    setDate(new Date().toISOString().split('T')[0]);
    setCategory('general');
    setValidUntil('');
    setImageUrl('');
    setImagePreview('');
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setSnackbar({ open: true, message: 'Please select a valid image file', severity: 'error' });
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setSnackbar({ open: true, message: 'Image size must be less than 2MB', severity: 'error' });
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setImageUrl(base64String);
        setImagePreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageUrl('');
    setImagePreview('');
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'urgent': return '#ff6b6b';
      case 'event': return '#4caf50';
      case 'closure': return '#ff9800';
      case 'general': return GOLD;
      default: return '#9e9e9e';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'urgent': return 'URGENT';
      case 'event': return 'EVENT';
      case 'closure': return 'CLOSURE';
      case 'general': return 'GENERAL';
      default: return 'OTHER';
    }
  };

  return (
    <Box>
      <Paper elevation={3} sx={{ p: 4, mb: 3, background: `linear-gradient(135deg, ${GOLD}15 0%, #fff 100%)` }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#2c3e50', mb: 1 }}>
              Notices Manager
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage temple notices, circulars, and official communications
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            sx={{ 
              bgcolor: GOLD, 
              color: '#000',
              fontWeight: 700,
              '&:hover': { bgcolor: '#c49b2e' }
            }}
          >
            Add Notice
          </Button>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: GOLD }} />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: `${GOLD}20` }}>
                <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Valid Until</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Typography color="text.secondary">
                      No notices found. Click "Add Notice" to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                notices.map((notice) => (
                  <TableRow key={notice.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{notice.title}</TableCell>
                    <TableCell>
                      <Chip
                        label={getCategoryLabel(notice.category)}
                        size="small"
                        sx={{
                          bgcolor: getCategoryColor(notice.category),
                          color: '#fff',
                          fontWeight: 700
                        }}
                      />
                    </TableCell>
                    <TableCell>{new Date(notice.date).toLocaleDateString('en-IN')}</TableCell>
                    <TableCell>
                      {notice.validUntil ? new Date(notice.validUntil).toLocaleDateString('en-IN') : 'N/A'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Typography variant="body2" noWrap>
                        {notice.description}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(notice)}
                        sx={{ color: GOLD, mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(notice.id)}
                        sx={{ color: '#f44336' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: `${GOLD}20`, fontWeight: 700 }}>
          {editingId ? 'Edit Notice' : 'Add New Notice'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Notice Title *"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Category *</InputLabel>
            <Select
              value={category}
              label="Category *"
              onChange={(e) => setCategory(e.target.value as Notice['category'])}
            >
              <MenuItem value="general">General</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
              <MenuItem value="event">Event</MenuItem>
              <MenuItem value="closure">Closure</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Description *"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={4}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="date"
            label="Notice Date *"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="date"
            label="Valid Until (Optional)"
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Leave empty if notice has no expiry date"
            sx={{ mb: 2 }}
          />
          <Box sx={{ my: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Notice Image (Optional)</Typography>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              sx={{ mb: 2, borderColor: GOLD, color: GOLD, '&:hover': { borderColor: '#e5c158', bgcolor: 'rgba(212,175,55,0.05)' } }}
            >
              Upload Image (Base64)
              <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
            </Button>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
              Supported: JPG, PNG, GIF. Max: 2MB
            </Typography>
            {(imagePreview || imageUrl) && (
              <Card sx={{ maxWidth: 400, mb: 2 }}>
                <CardMedia component="img" height="200" image={imagePreview || imageUrl} alt="Preview" sx={{ objectFit: 'cover' }} />
                <Box sx={{ p: 1, textAlign: 'center' }}>
                  <Button size="small" color="error" onClick={handleRemoveImage}>Remove Image</Button>
                </Box>
              </Card>
            )}
            <TextField
              fullWidth
              label="Or paste Image URL"
              value={imageUrl && !imageUrl.startsWith('data:') ? imageUrl : ''}
              onChange={(e) => { setImageUrl(e.target.value); setImagePreview(e.target.value); }}
              size="small"
              placeholder="https://example.com/image.jpg"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: '#666' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{ 
              bgcolor: GOLD, 
              color: '#000',
              fontWeight: 700,
              '&:hover': { bgcolor: '#c49b2e' }
            }}
          >
            {editingId ? 'Update' : 'Add'} Notice
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
